/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { Checkbox as CarbonCheckbox } from 'carbon-addons-iot-react';
import { useField } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import classnames from 'classnames';

import { FormLabelTooltip } from 'Common';

const propTypes = {
  tooltip: PropTypes.string,
  learnMore: PropTypes.string,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  hideLabel: PropTypes.bool,
  className: PropTypes.string,
  tooltipDirection: PropTypes.oneOf(['bottom', 'top']),
  tooltipAlign: PropTypes.oneOf(['start', 'center', 'end']),
};

const defaultProps = {
  tooltip: null,
  learnMore: null,
  hideLabel: false,
  className: null,
  tooltipDirection: 'bottom',
  tooltipAlign: 'start',
};

/**
 * Checkbox with added ability to show tooltip and interact with the redux-form
 */
const CheckboxFormik = ({
  tooltip,
  tooltipDirection,
  tooltipAlign,
  label,
  learnMore,
  className,
  hideLabel,
  id,
  ...other
}) => {
  const [field] = useField({ name: id, type: 'checkbox' });
  return (
    <div className={classnames('checkbox-row', className)}>
      <CarbonCheckbox
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...other} // pass-through to Carbon so I can support all future props
        checked={Boolean(field.value)}
        labelText={label}
        hideLabel
        onChange={value => {
          field.onChange({
            type: 'change',
            target: { value, name: id },
          });
        }}
        id={id}
      />
      {!hideLabel ? (
        <FormLabelTooltip
          label={!tooltip ? <span className="bx--label">{label}</span> : label}
          tooltip={tooltip}
          tooltipDirection={tooltipDirection}
          tooltipAlign={tooltipAlign}
          learnMore={learnMore}
          required
        />
      ) : null}
    </div>
  );
};

CheckboxFormik.propTypes = propTypes;
CheckboxFormik.defaultProps = defaultProps;

export default CheckboxFormik;
